import React from 'react';
import { Layout } from '../components/layout/Layout';
import { SeoHead } from '../components/ui/SeoHead';
import { Section } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageNavigator } from '../components/ui/PageNavigator';
import { CheckCircle, Activity, Truck, Settings, FileText, Shield, ClipboardCheck, ArrowRight, Zap, Target, Globe, Clock, Droplets, FlaskConical, Wrench, Package, BarChart3 } from 'lucide-react';
import { VisualBreak } from '../components/ui/VisualBreak';
import { KPIStrip } from '../components/ui/KPIStrip';
import { PowerStatement } from '../components/ui/PowerStatement';

export const Services = () => {
  const sections = [
    { id: 'well-testing', label: 'Well Testing Equipment' },
    { id: 'stimulation', label: 'Stimulation & Nitrogen' },
    { id: 'field-services', label: 'Field Services' },
    { id: 'equipment-rental', label: 'Support Equipment' },
    { id: 'filtration', label: 'Filtration & Water Treatment' },
    { id: 'chemicals', label: 'Oilfield Chemicals' },
    { id: 'logistics', label: 'Logistics & Transport' },
    { id: 'digital-data', label: 'Digital & Data Solutions' },
    { id: 'execution-capacity', label: 'Execution Capacity' },
    { id: 'why-energipro', label: 'Why EnergiPro' },
  ];

  return (
    <Layout>
      <SeoHead
        title="Operational Capabilities | Well Testing, Stimulation & Field Services"
        description="Seven integrated service lines: surface well testing, stimulation, field services, heavy equipment, filtration, oilfield chemicals, and logistics. Mobilization within 48-72 hours."
        canonicalPath="/services"
      />
      {/* Hero Section */}
      <div className="bg-slate-900 pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-l-4 border-orange-600 pl-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">OPERATIONAL CAPABILITIES</h1>
            <div className="flex flex-col gap-3 mb-6">
              <p className="text-lg text-slate-300">
                <span className="text-orange-400 font-bold">Pressure Rating:</span> 10,000 PSI & 1,440 PSI equipment configurations
              </p>
              <p className="text-lg text-slate-300">
                <span className="text-orange-400 font-bold">Core Capability:</span> Surface well testing, stimulation, nitrogen pumping, filtration, chemical treatment, field execution
              </p>
              <p className="text-lg text-slate-300">
                <span className="text-orange-400 font-bold">Geography:</span> KSA (Eastern Province), UAE (Abu Dhabi, Dubai), Egypt (Western Desert, Gulf of Suez)
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button href="/request-quote" variant="primary" className="px-8 py-3">
                SUBMIT RFQ
              </Button>
              <Button href="/files/energipro-capability-statement.pdf" variant="outline-light" className="px-8 py-3">
                REQUEST TECHNICAL CAPABILITY PACKAGE
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Strip after hero */}
      <KPIStrip
        background="accent"
        items={[
          { value: '10K', suffix: ' PSI', label: 'Max Pressure Rating' },
          { value: '48–72', suffix: ' hrs', label: 'Mobilization Time' },
          { value: '7', label: 'Integrated Service Lines' },
          { value: '3', label: 'Regional Equipment Yards' },
        ]}
      />

      <PageNavigator sections={sections} />

      {/* ===== 1. SURFACE WELL TESTING EQUIPMENT ===== */}
      <Section id="well-testing">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="text-orange-600" size={36} />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">SURFACE WELL TESTING EQUIPMENT</h2>
          </div>
          <p className="text-slate-600 max-w-4xl text-lg">
            Owned equipment. Rated 10,000 PSI and 1,440 PSI working pressure. Skid-mounted and trailer-mounted. Pressure-tested and certified before each mobilization.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-2">Test Separators</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>2-phase & 3-phase horizontal separators</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>WP: 1,440 PSI | Capacity: 5,000–15,000 BFPD</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Integrated level controls, relief valves, dump valves</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Use: well cleanup, EWT, production testing, flowback</span></li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-2">Choke Manifolds</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>WP: 10,000 PSI | Bore: 3-1/16"</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Dual-path: adjustable + positive choke beans</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Wellhead pressure regulation during flow & shut-in</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>API 6A compliant design</span></li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-2">Flare Systems & Data Acquisition</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Flare boom: H₂S rated, multi-burner, 100 MMSCFD capacity</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Pilot ignition, flame arrestors, wind guards</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Data acquisition: P/T/flow @ 1-sec intervals</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Remote transmission to client SCADA/DCS</span></li>
            </ul>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 p-6 rounded-sm">
          <h4 className="font-bold text-slate-900 mb-3">Additional Well Testing Equipment</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-slate-700">
            <div>• Surface safety valves (10K PSI)</div>
            <div>• Flow headers (10K PSI)</div>
            <div>• Sand traps / desanders</div>
            <div>• Surge tanks (100 BBL)</div>
            <div>• Offshore tanks (500 BBL)</div>
            <div>• Transfer pumps (centrifugal/positive)</div>
            <div>• Heat exchangers (indirect line heaters)</div>
            <div>• Emergency shut-down systems (ESD)</div>
            <div>• HP piping, fittings & swivel joints</div>
          </div>
        </div>
      </Section>

      {/* Visual Break between sections */}
      <VisualBreak
        image="/assets/showcase/batch-4/well-test-separator-trailer.png"
        statement="Owned & operated equipment. Pre-inspected before every mobilization."
        height="sm"
      />

      {/* ===== 2. STIMULATION & NITROGEN EQUIPMENT ===== */}
      <Section id="stimulation" background="steel">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Zap className="text-orange-600" size={36} />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">STIMULATION & NITROGEN EQUIPMENT</h2>
          </div>
          <p className="text-slate-600 max-w-4xl text-lg">
            HP nitrogen pumping units rated to 10,000 PSI. Deployed for well unloading, pipeline purging, pressure testing, and stimulation support. Operated by our crews.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-2">Nitrogen Pumping Units</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>WP: 10,000 PSI | Flow: 1,200 SCFM max</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Truck-mounted, twin-pump configuration</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Use: well kick-off, unloading, pipeline inerting</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Integrated chart recorders & pressure relief</span></li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-2">Frac Tanks & Fluid Storage</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Capacity: 500 BBL per tank, stackable</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Use: flowback storage, stimulation fluid, brine</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Level indicators, drain valves, secondary containment</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Fleet: 20+ tanks available across 3 yards</span></li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-2">HP Pumping & Pressure Testing</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Test pressure: up to 15,000 PSI</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Hydrostatic test packages (pipeline & vessel)</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Certified chart recorders (API 598 / ASME B31.3)</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Coiled tubing support & cementing pump packages</span></li>
            </ul>
          </div>
        </div>
      </Section>

      {/* ===== 3. FIELD SERVICES & OPERATIONAL SUPPORT ===== */}
      <Section id="field-services">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Settings className="text-orange-600" size={36} />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">FIELD EXECUTION & EQUIPMENT OPERATIONS</h2>
          </div>
          <p className="text-slate-600 max-w-4xl text-lg">
            Our crews operate our equipment. Rig-up through demob under single-point responsibility. No third-party operator hand-offs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-3">Equipment Operations — Wellsite</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Rig-up/rig-down of full test spread (separator, choke, flare, piping)</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Choke management & flow rate control during testing</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Real-time data acquisition: P, T, flow rate, GOR, BS&W</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>H₂S monitoring & emergency response procedures active</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>End-of-well reports delivered within 24 hrs of demob</span></li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-3">Equipment Integrity & Maintenance</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Pressure testing before each mob (hydro + function test)</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Third-party NDT inspection (MPI, UT, visual)</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Maintenance logs & certification records per equipment</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Valve overhaul & torque testing to API 6A intervals</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>In-house workshop: fabrication, assembly, refurbishment</span></li>
            </ul>
          </div>
        </div>
      </Section>

      {/* ===== 4. WELLSITE SUPPORT EQUIPMENT ===== */}
      <Section id="equipment-rental" background="steel">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Wrench className="text-orange-600" size={36} />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">WELLSITE SUPPORT EQUIPMENT</h2>
          </div>
          <p className="text-slate-600 max-w-4xl text-lg">
            Support equipment deployed alongside our primary service lines. Eliminates separate vendor mobilization for ancillary requirements at wellsite.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-2">Lifting & Rigging</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li>• Mobile cranes: 25T, 50T, 70T, 100T</li>
              <li>• Boom trucks & telehandlers</li>
              <li>• Use: equipment rig-up, skid placement, pipe handling</li>
              <li>• Certified operators & rigging plans provided</li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-2">Power & Air</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li>• Generators: 50 kVA – 500 kVA (diesel)</li>
              <li>• Air compressors: 375–900 CFM @ 150 PSI</li>
              <li>• Light towers: 4×1000W metal halide</li>
              <li>• Use: wellsite power, instrument air, night ops lighting</li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-2">Vehicles & Fluid Handling</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li>• 4×4 pickup trucks (crew & supervision)</li>
              <li>• Water tankers: 3,000–10,000 gal</li>
              <li>• Vacuum trucks: produced fluid removal</li>
              <li>• Use: crew transport, fluid disposal, dust control</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* Visual Break */}
      <VisualBreak
        image="/assets/showcase/batch-3/desert-camp-modules.png"
        statement="Full wellsite support — from equipment to accommodation to chemicals."
        height="sm"
      />

      {/* ===== 5. FILTRATION & WATER TREATMENT ===== */}
      <Section id="filtration">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Droplets className="text-orange-600" size={36} />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">FILTRATION & WATER TREATMENT SYSTEMS</h2>
          </div>
          <p className="text-slate-600 max-w-4xl text-lg">
            Skid-mounted filtration and separation units deployed at wellsite. Integrated with our well testing and flowback operations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-3">Equipment</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Multi-media filtration skids — 500–5,000 BPD throughput</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Cartridge filters: 1µm – 50µm particle removal</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Oil/water separators: CPI & hydrocyclone type</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Sand management: desanders, desilters</span></li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-3">Operational Use</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Injection water prep (meets reservoir quality specs)</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Completion/workover fluid filtration</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Flowback water treatment prior to disposal/reinjection</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Deployed integrated with our test spread — single mob</span></li>
            </ul>
          </div>
        </div>
      </Section>

      {/* ===== 6. OILFIELD CHEMICALS — OPERATIONS INTEGRATED ===== */}
      <Section id="chemicals" background="steel">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <FlaskConical className="text-orange-600" size={36} />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">OILFIELD CHEMICALS — OPERATIONS INTEGRATED</h2>
          </div>
          <p className="text-slate-600 max-w-4xl text-lg">
            Chemicals selected and dosed as part of our well testing and production operations. Not a standalone supply — integrated into our execution scope when required.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-2">Corrosion & Scale Control</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li>• Corrosion inhibitors (filming amine, imidazoline)</li>
              <li>• Scale inhibitors (phosphonate, polymer-based)</li>
              <li>• Oxygen scavengers (ammonium bisulfite)</li>
              <li>• Applied during: EWT, flowback, production testing</li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-2">Production Treatment</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li>• Demulsifiers (oil/water separation aid)</li>
              <li>• H₂S scavengers (triazine-based, iron sponge)</li>
              <li>• Paraffin inhibitors (crystal modifier type)</li>
              <li>• Dosing: continuous injection or batch treatment</li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-2">Water Treatment Chemicals</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li>• Biocides (glutaraldehyde, THPS)</li>
              <li>• Flocculants & coagulants (polyDADMAC)</li>
              <li>• Reverse emulsion breakers</li>
              <li>• Coordinated with our filtration equipment on location</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* ===== 7. LOGISTICS & TRANSPORTATION SUPPORT ===== */}
      <Section id="logistics">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Truck className="text-orange-600" size={36} />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">LOGISTICS & EQUIPMENT MOBILIZATION</h2>
          </div>
          <p className="text-slate-600 max-w-4xl text-lg">
            Own transport fleet. Equipment moved from our yards to wellsite under our control. Cross-border coordination handled internally (KSA ↔ UAE ↔ Egypt).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-3">Transport Fleet</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Lowboy trailers: 60T–100T payload capacity</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Flatbed & drop-deck: skid packages, piping bundles</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Crane trucks: 25T–50T for self-loading/unloading</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Oversized permits, pilot vehicles, route surveys</span></li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-3">Mobilization Scope</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Mob/demob planning: load lists, transport method statements</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Cross-border: customs clearance, carnets, transit permits</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Equipment yards: Dammam, Dubai, Cairo (staging & storage)</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Spare parts warehousing & consignment management</span></li>
            </ul>
          </div>
        </div>
      </Section>

      {/* ===== 8. DIGITAL & DATA SOLUTIONS — OPERATIONAL SUPPORT ===== */}
      <Section id="digital-data" background="steel">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="text-orange-600" size={36} />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">DIGITAL & DATA SOLUTIONS</h2>
          </div>
          <p className="text-slate-600 max-w-4xl text-lg">
            Operational support tools deployed alongside our field services. Data acquisition, monitoring, and reporting capabilities integrated into our execution scope.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-3">Data Acquisition & Monitoring</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Data acquisition systems — pressure, temperature, flow rate at configurable intervals</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Real-time monitoring and reporting during field operations</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Operational performance tracking across equipment and crews</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Remote data transmission to client SCADA/DCS systems</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Drilling reports analytics tool — structured analysis of daily drilling data</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Field surveillance systems — remote monitoring of wellsite conditions and equipment status</span></li>
            </ul>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm">
            <h4 className="font-bold text-slate-900 mb-3">Digital Reporting & Deliverables</h4>
            <ul className="text-slate-600 text-sm space-y-2">
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Digital reporting packages for client deliverables</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Automated daily operation summaries</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>End-of-well and end-of-job digital data packages</span></li>
              <li className="flex items-start gap-2"><CheckCircle className="text-orange-500 shrink-0 mt-0.5" size={14} /><span>Integrated with our field execution for seamless data capture</span></li>
            </ul>
          </div>
        </div>

        <div className="mt-6 bg-slate-50 border border-slate-200 p-5 rounded-sm">
          <p className="text-slate-500 text-sm italic">
            Note: Digital & Data Solutions are deployed as operational support tools integrated with our primary service lines — not offered as a standalone technology service.
          </p>
        </div>
      </Section>

      {/* Power Statement */}
      <PowerStatement text="API-aligned execution. No deviation without Management of Change." variant="dark" />

      {/* ===== 8. EXECUTION CAPACITY ===== */}
      <Section id="execution-capacity" background="steel">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Target className="text-orange-600" size={36} />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">EXECUTION CAPACITY</h2>
          </div>
          <p className="text-slate-600 max-w-4xl text-lg">
            Current fleet and crew size supports concurrent operations across multiple wellsites.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white border border-slate-200 p-5 rounded-sm border-t-4 border-t-orange-600">
            <h4 className="font-bold text-slate-900 mb-3">Multi-Well Campaigns</h4>
            <p className="text-slate-600 text-sm">Executed 5+ well campaigns on continuous rotation without equipment downtime. Back-to-back well testing with same spread.</p>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm border-t-4 border-t-orange-600">
            <h4 className="font-bold text-slate-900 mb-3">Pressure Capability</h4>
            <p className="text-slate-600 text-sm">Full spread rated to 10,000 PSI WP — choke manifold through flowline to separator. 1,440 PSI separation & storage.</p>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm border-t-4 border-t-orange-600">
            <h4 className="font-bold text-slate-900 mb-3">Simultaneous Operations</h4>
            <p className="text-slate-600 text-sm">Fleet supports 2–3 concurrent well test spreads deployed simultaneously across different wellsites or fields.</p>
          </div>
          <div className="bg-white border border-slate-200 p-5 rounded-sm border-t-4 border-t-orange-600">
            <h4 className="font-bold text-slate-900 mb-3">Regional Deployment</h4>
            <p className="text-slate-600 text-sm">Equipment yards in 3 countries. Mobilization within 48–72 hrs from RFQ acceptance. No single-source bottleneck.</p>
          </div>
        </div>
      </Section>

      {/* ===== WHY ENERGIPRO ===== */}
      <Section id="why-energipro" background="dark">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">WHY ENERGIPRO</h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Proof-based differentiators for procurement evaluation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm">
              <Package className="text-orange-500 mb-3" size={28} />
              <h4 className="text-white font-bold mb-2">Operational Control</h4>
              <p className="text-slate-400 text-sm">We own the equipment. We operate it with our crews. Single point of responsibility — no sub-rental, no third-party operators on your wellsite.</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm">
              <Target className="text-orange-500 mb-3" size={28} />
              <h4 className="text-white font-bold mb-2">Execution Discipline</h4>
              <p className="text-slate-400 text-sm">Every job follows: mob plan → rig-up procedure → operational checklist → daily reporting → demob & closeout report. Documented and auditable.</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm">
              <Zap className="text-orange-500 mb-3" size={28} />
              <h4 className="text-white font-bold mb-2">Mobilization: 48–72 Hours</h4>
              <p className="text-slate-400 text-sm">Pre-staged equipment in Dammam, Dubai, Cairo. Transport fleet owned. No dependency on third-party logistics for mobilization.</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm">
              <FileText className="text-orange-500 mb-3" size={28} />
              <h4 className="text-white font-bold mb-2">Audit-Ready Documentation</h4>
              <p className="text-slate-400 text-sm">Pressure test certs, NDT reports, maintenance logs, competency records, JSA/PTW — all current, all available for review before or during job.</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm">
              <Shield className="text-orange-500 mb-3" size={28} />
              <h4 className="text-white font-bold mb-2">Equipment Certification</h4>
              <p className="text-slate-400 text-sm">All pressure-rated equipment carries current hydrostatic test certificate. Third-party inspection per API 6A / ASME intervals. Records travel with equipment.</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 p-6 rounded-sm">
              <ClipboardCheck className="text-orange-500 mb-3" size={28} />
              <h4 className="text-white font-bold mb-2">Single Commercial Interface</h4>
              <p className="text-slate-400 text-sm">Equipment + crews + chemicals + transport + filtration on one PO. One mobilization. One demob. Reduces your vendor coordination overhead.</p>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== FINAL CTA ===== */}
      <Section background="navy" className="border-t-2 border-orange-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            SUBMIT RFQ OR REQUEST TECHNICAL CAPABILITY PACKAGE
          </h2>
          <p className="text-slate-300 mb-8 text-lg">
            Commercial team responds within 24 business hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" href="/request-quote">
              SUBMIT RFQ
            </Button>
            <Button size="lg" variant="outline-light" href="/files/energipro-capability-statement.pdf">
              DOWNLOAD TECHNICAL CAPABILITY PACKAGE
            </Button>
          </div>
        </div>
      </Section>
    </Layout>
  );
};
